import { UpperCasePipe } from '@angular/common';
import { Component, OnDestroy, OnInit, inject, input, output } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmLabel } from '@spartan-ng/helm/label';
import { BrnSelectImports } from '@spartan-ng/brain/select';
import { HlmSelectImports } from '@spartan-ng/helm/select';
import { User, UserRole } from '@core/models/users';
import { Subscription, map, tap } from 'rxjs';
import { minOptionSelected } from 'src/app/admin/management/validators/minOptionSelected';
import { WhoAmIService } from 'src/app/auth/services/whoami.service';
import { environment } from '../../../../environments/environment';
@Component({
  selector: 'app-user-details[user]',
  template: `
    <form [formGroup]="userDetailsForm" class="row">
      @if (!isUserView()) {
        @if (!isLockedUser()) {
          @if (!isAdmin()) {
            <div class="col-6">
              <label hlmLabel>Roles</label>
              <brn-select [multiple]="true" formControlName="roles" class="w-full">
                <hlm-select-trigger>
                  <hlm-select-value />
                </hlm-select-trigger>
                <hlm-select-content>
                  @for (role of roles; track role) {
                    <hlm-option 
                      [disabled]="role === lockedRole" 
                      [value]="role"
                      (click)="handleSelectionChange(role)"
                      >{{ 'ROLES.' + role | uppercase }}</hlm-option>
                  }
                </hlm-select-content>
              </brn-select>
            </div>
          }
        } @else {
          <p class="text-warning text-center mb-4">User has not completed registration</p>
        }

        @if (!isAdmin()) {
          <div class="col-6 flex items-center gap-4" role="radiogroup" aria-labelledby="status">
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                [value]="true"
                formControlName="enabled"
                class="w-4 h-4 text-primary border-gray-300 focus:ring-2 focus:ring-primary dark:border-gray-600 dark:bg-gray-700 dark:focus:ring-primary cursor-pointer"
              />
              <span class="text-sm font-medium text-gray-900 dark:text-gray-100">Enabled</span>
            </label>
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                [value]="false"
                formControlName="enabled"
                class="w-4 h-4 text-primary border-gray-300 focus:ring-2 focus:ring-primary dark:border-gray-600 dark:bg-gray-700 dark:focus:ring-primary cursor-pointer"
              />
              <span class="text-sm font-medium text-gray-900 dark:text-gray-100">Disabled</span>
            </label>
          </div>
        }
      }

      <div class="col-12 mt-3">
        <label hlmLabel>Email</label>
        <input hlmInput name="email" formControlName="email" class="w-full" />
      </div>

      <section formGroupName="profile">
        <div class="col-12 mt-3">
          <label hlmLabel>Name</label>
          <input hlmInput name="name" formControlName="name" class="w-full" />
        </div>

        <div class="col-12 mt-3">
          <label hlmLabel>Surname</label>
          <input hlmInput name="surname" formControlName="surname" class="w-full" />
        </div>
      </section>
    </form>
  `,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    ...BrnSelectImports,
    ...HlmSelectImports,
    ...HlmInputImports,
    HlmLabel,
    UpperCasePipe,
  ],
})
export class UserDetailsComponent implements OnInit, OnDestroy {
  private fb = inject(UntypedFormBuilder);

  readonly user = input.required<User>();

  readonly isUserView = input<boolean>(false);

  readonly disabledFields = input<(keyof User)[]>([]);

  readonly formValueChange = output<any>();

  readonly formStateChange = output<boolean>();

  userDetailsForm!: UntypedFormGroup;

  private whoami = inject(WhoAmIService);

  roles = [UserRole.LOCKED_USER, UserRole.ADMIN, UserRole.USER];

  lockedRole = UserRole.LOCKED_USER;

  subs: Subscription = new Subscription();

  i18nEnabled = environment.ENABLE_I18N;

  ngOnInit(): void {
    const user = this.user();

    this.userDetailsForm = this.fb.group({
      roles: [{ value: user?.roles, disabled: this.isAdmin() }, [Validators.required, minOptionSelected(1)]],
      enabled: [user?.enabled],
      email: [user._id],
      profile: this.fb.group({
        name: [user.profile?.name ?? ''],
        surname: [user.profile?.surname ?? ''],
      }),
    });

    this.userDetailsForm.get('email')?.disable();

    this.userDetailsForm.get('tenants')?.disable();

    if (this.isUserView()) {
      this.userDetailsForm.get('roles')?.disable();
      this.userDetailsForm.get('enabled')?.disable();
    }

    Object.values(this.disabledFields()).forEach(key => {
      this.userDetailsForm.get(key)?.disable();
    });

    this.userDetailsForm.get('phone')?.markAsTouched();

    const valueSub = this.userDetailsForm.valueChanges
      .pipe(tap(values => this.formValueChange.emit(values)))
      .subscribe();

    const stateSub = this.userDetailsForm.statusChanges
      .pipe(
        map(state => state === 'VALID'),
        tap(state => this.formStateChange.emit(state))
      )
      .subscribe();

    this.subs.add(valueSub);
    this.subs.add(stateSub);
  }

  handleSelectionChange(incomingRole: UserRole) {
    // Use setTimeout to ensure the form control value is updated
    setTimeout(() => {
      const currentRoles: string[] | null = this._roles.value;

      if (currentRoles && currentRoles.includes(incomingRole)) {
        if (incomingRole === UserRole.ADMIN) {
          this._roles.setValue([UserRole.ADMIN]);
        } else if (currentRoles.includes(UserRole.ADMIN)) {
          this._roles.setValue([incomingRole, ...currentRoles.filter(role => role !== UserRole.ADMIN)]);
        }
      }
    });
  }

  get _roles(): UntypedFormControl {
    return this.userDetailsForm.get('roles') as UntypedFormControl;
  }

  isLockedUser(): boolean {
    return this.user()
      .roles.map(r => r.toString())
      .includes('locked-user');
  }

  isAdmin(): boolean {
    const user = this.user();
    return user.roles.length === 1 && user.roles[0] === UserRole.ADMIN;
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  compareTenants(tenant1: any, tenant2: any): boolean {
    return tenant1?._id === tenant2?._id ? true : false;
  }
}
